import React, {useRef, useState} from 'react';
import {injectIntl} from "react-intl";
import Popover, {PopoverHeader} from "@amzn/meridian/popover";
import './Inbound.css';

const MAX_VISIBLE_ROWS = 3;

/**
 * Formats the planned-arrival value from the LINEHAUL_DETAILS displayName
 * (e.g. "2026-07-24T03:29") down to the HH:MM portion for compact display.
 * The backend emits this already in station-local time, so we display the
 * time portion verbatim (no timezone conversion).
 */
function formatPlanned(plannedArrival) {
    if (!plannedArrival) return '—';
    const str = String(plannedArrival);
    const tIndex = str.indexOf('T');
    if (tIndex === -1) return str;
    return str.slice(tIndex + 1, tIndex + 6) || str;
}

const LinehaulRow = ({row, showXsS, showPlanned}) => (
    <tr>
        <td className="lh-id">{row.lineHaulId}</td>
        <td>{typeof row.cube === 'number' ? row.cube.toFixed(2) : '—'}</td>
        <td>{(row.packageCount || 0).toLocaleString()}</td>
        {showXsS && (
            <td>{typeof row.percentExtraSmallAndSmall === 'number' ? row.percentExtraSmallAndSmall + '%' : '—'}</td>
        )}
        {showPlanned && (
            <td>{formatPlanned(row.plannedArrival)}</td>
        )}
    </tr>
);

const LinehaulTable = ({rows, showXsS, showPlanned, intl}) => (
    <table className="linehaul-table">
        <thead>
            <tr>
                <th>{intl.formatMessage({ id: 'inbound.cube.col_linehaul_id', defaultMessage: 'Linehaul ID' })}</th>
                <th>{intl.formatMessage({ id: 'inbound.cube.col_cube', defaultMessage: 'Cube' })}</th>
                <th>{intl.formatMessage({ id: 'inbound.cube.col_pkg_count', defaultMessage: 'Pkg Count' })}</th>
                {showXsS && <th>{intl.formatMessage({ id: 'inbound.cube.col_xs_s', defaultMessage: '% XS/S' })}</th>}
                {showPlanned && <th>{intl.formatMessage({ id: 'inbound.cube.col_planned_arrival', defaultMessage: 'Planned Arrival' })}</th>}
            </tr>
        </thead>
        <tbody>
            {rows.map(row => (
                <LinehaulRow key={row.lineHaulId} row={row} showXsS={showXsS} showPlanned={showPlanned}/>
            ))}
        </tbody>
    </table>
);

const LinehaulSection = ({title, rows, showXsS, showPlanned, intl}) => {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const anchorRef = useRef(null);
    const visibleRows = rows.slice(0, MAX_VISIBLE_ROWS);
    const remainingCount = rows.length - visibleRows.length;

    return (
        <div className="linehaul-section">
            <div className="linehaul-header">
                <span>{title}</span>
                {rows.length > 0 && <span className="lh-count">{rows.length}</span>}
            </div>
            <div className="linehaul-table-wrap">
                <LinehaulTable rows={visibleRows} showXsS={showXsS} showPlanned={showPlanned} intl={intl}/>
            </div>
            {remainingCount > 0 && (
                <React.Fragment>
                    <button
                        type="button"
                        className="lh-more-trigger"
                        ref={anchorRef}
                        onClick={() => setPopoverOpen(true)}
                    >
                        <span>{intl.formatMessage({ id: 'inbound.cube.more', defaultMessage: '+ {count} more' }, { count: remainingCount })}</span>
                        <span className="lh-caret">▾</span>
                    </button>
                    <Popover
                        anchorNode={anchorRef.current}
                        position="bottom"
                        alignment="center"
                        open={popoverOpen}
                        onClose={() => setPopoverOpen(false)}
                        maxWidth={420}
                        minWidth="anchor"
                    >
                        <PopoverHeader closeLabel={intl.formatMessage({ id: 'inbound.cube.popover_close', defaultMessage: 'Close' })}>
                            {intl.formatMessage({ id: 'inbound.cube.all_linehauls', defaultMessage: 'All {count} linehauls' }, { count: rows.length })}
                        </PopoverHeader>
                        <LinehaulTable rows={rows} showXsS={showXsS} showPlanned={showPlanned} intl={intl}/>
                    </Popover>
                </React.Fragment>
            )}
        </div>
    );
};

const StatusCell = ({entry}) => (
    <React.Fragment>
        <td>{entry ? (entry.count || 0).toLocaleString() : '—'}</td>
        <td>{entry && typeof entry.cube === 'number' ? entry.cube.toFixed(2) : '—'}</td>
    </React.Fragment>
);

/**
 * Grid of linehaul-detail tables shown beneath the Cube Analysis cards, column-aligned
 * 1:1 with (Pending Depart, In Transit, Pending Induct, Pending Stow, Stow Cube).
 * Driven entirely by the GetMetricData cube response (via CubeAnalysisSection):
 *  - Pending Depart: per-linehaul cube / pkg count / planned arrival
 *  - In Transit:     per-linehaul cube / pkg count / %XS/S / planned arrival
 *  - Arrived & Received: pkg count + avg cube per status
 * Pending Stow / Stow Cube have no linehaul breakdown, so those slots render empty.
 */
const LinehaulDetailsGrid = ({linehaulData, intl}) => {
    if (!linehaulData) return null;

    const pendingDepart = linehaulData.pendingDepart || [];
    const inTransit = linehaulData.inTransit || [];
    const arrivedReceived = linehaulData.arrivedReceived || null;

    // Nothing to show at all — don't render an empty grid.
    if (pendingDepart.length === 0 && inTransit.length === 0 && !arrivedReceived) {
        return null;
    }

    const pendingDepartTitle = intl.formatMessage({ id: 'inbound.cube.pending_depart', defaultMessage: 'Pending Depart' });
    const inTransitTitle = intl.formatMessage({ id: 'inbound.cube.in_transit', defaultMessage: 'In Transit' });

    return (
        <div className="linehaul-grid">
            <LinehaulSection title={pendingDepartTitle} rows={pendingDepart} showXsS={false} showPlanned={true} intl={intl}/>
            <LinehaulSection title={inTransitTitle} rows={inTransit} showXsS={true} showPlanned={true} intl={intl}/>
            <div className="linehaul-section">
                <div className="linehaul-header">
                    <span>{intl.formatMessage({ id: 'inbound.cube.arrived_received', defaultMessage: 'Arrived & Received' })}</span>
                </div>
                <div className="linehaul-table-wrap">
                    <table className="linehaul-table">
                        <thead>
                            <tr>
                                <th/>
                                <th>{intl.formatMessage({ id: 'inbound.cube.col_pkg_count', defaultMessage: 'Pkg Count' })}</th>
                                <th>{intl.formatMessage({ id: 'inbound.cube.col_cube', defaultMessage: 'Cube' })}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="lh-id">{intl.formatMessage({ id: 'inbound.cube.arrived', defaultMessage: 'Arrived' })}</td>
                                <StatusCell entry={arrivedReceived && arrivedReceived.arrived}/>
                            </tr>
                            <tr>
                                <td className="lh-id">{intl.formatMessage({ id: 'inbound.cube.received', defaultMessage: 'Received' })}</td>
                                <StatusCell entry={arrivedReceived && arrivedReceived.received}/>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="linehaul-slot-empty"/>
            <div className="linehaul-slot-empty"/>
        </div>
    );
};

export default injectIntl(LinehaulDetailsGrid);



// WEBPACK FOOTER //
// ./src/components/inbound/LinehaulDetailsGrid.js